import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  TrendUp, Clock, Target, Users, ArrowRight, ChartLineUp, Money,
  Bell, Check, Trash, ArrowsClockwise, MagnifyingGlass, WarningCircle,
  CheckCircle, Calendar, ArrowSquareOut, Sparkle, Wrench, ShieldCheck,
  Hourglass, FileText, CheckSquareOffset, Buildings
} from "@phosphor-icons/react";
import WhatsAppConnection from "@/components/WhatsAppConnection";

const STATUS_LABELS = {
  new: "New", contacted: "Contacted", initial: "Initial", fu1: "Follow-up 1",
  fu2: "Follow-up 2", fu3: "Follow-up 3", fu4: "Follow-up 4", fu5: "Follow-up 5", converted: "Converted", lost: "Lost",
  quotation_sent: "Quote Sent", qfu1: "Quote FU-1", qfu2: "Quote FU-2", qfu3: "Quote FU-3", qfu4: "Quote FU-4", qfu5: "Quote FU-5",
};

const STATUS_COLORS = {
  new: "#3B82F6", contacted: "#6B7280", initial: "#6366F1", fu1: "#F59E0B",
  fu2: "#F97316", fu3: "#EF4444", fu4: "#DC2626", fu5: "#991B1B", converted: "#10B981", lost: "#9CA3AF",
  quotation_sent: "#8B5CF6", qfu1: "#A78BFA", qfu2: "#8B5CF6", qfu3: "#7C3AED", qfu4: "#6D28D9", qfu5: "#4C1D95",
};

const CHANNEL_LABELS = {
  walk_in: "Walk In",
  meta: "Meta Ads",
  indiamart: "IndiaMART",
  telephonic: "Telephonic",
  csv_upload: "CSV Upload",
  google_form: "Google Form",
  other: "Other",
};

const CHANNEL_COLORS = {
  walk_in: "#3B82F6",
  meta: "#1877F2",
  indiamart: "#10B981",
  telephonic: "#F59E0B",
  csv_upload: "#8B5CF6",
  google_form: "#EC4899",
  other: "#6B7280",
};

const CONTRACT_TYPE_LABELS = {
  warranty: "Warranty",
  out_of_warranty: "Out of Warranty",
  amc: "AMC",
  out_of_amc: "Out of AMC",
  on_call: "On Call",
  register: "Register",
  other: "Other",
};

const CONTRACT_TYPE_COLORS = {
  warranty: "#002FA7",
  out_of_warranty: "#D97706",
  amc: "#7C3AED",
  out_of_amc: "#DC2626",
  on_call: "#059669",
  register: "#4F46E5",
  other: "#6B7280",
};

const CONTRACT_STATUS_LABELS = {
  active: "Active",
  completed: "Completed",
  expired: "Expired",
  renewed: "Renewed",
  declined: "Declined",
};

const CONTRACT_STATUS_COLORS = {
  active: "#10B981",
  completed: "#3B82F6",
  expired: "#EF4444",
  renewed: "#7C3AED",
  declined: "#6B7280",
};

const EMPTY_DASHBOARD_STATS = {
  total_leads: 0,
  due_today: 0,
  converted_rate: 0,
  total_amount: 0,
  by_status: {},
  by_source: {},
  by_channel: {},
  top_due_leads: [],
  team: [],
  lost_reminders_count: 0,
  services: {
    total_contracts: 0,
    active_contracts: 0,
    expiring_soon: 0,
    expired: 0,
    due_today: 0,
    total_amount: 0,
    by_type: {},
    by_status: {},
    top_due_services: [],
  },
};

const PIPELINE_PHASES = [
  {
    id: "discovery",
    title: "1. Inbound & Initial",
    accent: "#3B82F6",
    bgAccent: "bg-blue-50/40",
    borderAccent: "border-blue-100",
    stages: [
      { key: "new", label: "New" },
      { key: "contacted", label: "Contacted" },
      { key: "initial", label: "Initial" },
    ],
  },
  {
    id: "followups",
    title: "2. Follow-up Cycle",
    accent: "#F59E0B",
    bgAccent: "bg-amber-50/40",
    borderAccent: "border-amber-100",
    stages: [
      { key: "fu1", label: "FU 1" },
      { key: "fu2", label: "FU 2" },
      { key: "fu3", label: "FU 3" },
      { key: "fu4", label: "FU 4" },
      { key: "fu5", label: "FU 5" },
    ],
  },
  {
    id: "quotations",
    title: "3. Quotation Funnel",
    accent: "#8B5CF6",
    bgAccent: "bg-purple-50/40",
    borderAccent: "border-purple-100",
    stages: [
      { key: "quotation_sent", label: "Quote Sent" },
      { key: "qfu1", label: "Quote FU-1" },
      { key: "qfu2", label: "Quote FU-2" },
      { key: "qfu3", label: "Quote FU-3" },
      { key: "qfu4", label: "Quote FU-4" },
      { key: "qfu5", label: "Quote FU-5" },
    ],
  },
  {
    id: "outcomes",
    title: "4. Outcomes",
    accent: "#10B981",
    bgAccent: "bg-emerald-50/40",
    borderAccent: "border-emerald-100",
    stages: [
      { key: "converted", label: "Converted" },
      { key: "lost", label: "Lost" },
    ],
  },
];

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [whatsAppConnected, setWhatsAppConnected] = useState(true);
  const [emailFollowupEnabled, setEmailFollowupEnabled] = useState(true);
  const [lostReminders, setLostReminders] = useState([]);
  const [reminderFilter, setReminderFilter] = useState("all");
  const [reminderSearch, setReminderSearch] = useState("");

  const userDept = (user?.department || "all").toLowerCase();
  const isSalesperson = user?.role === "salesperson";
  const canAccessLeads = !isSalesperson || userDept === "lead" || userDept === "all" || userDept === "both";
  const canAccessServices = !isSalesperson || userDept === "service" || userDept === "all" || userDept === "both";

  const defaultTab = !canAccessServices ? "leads" : (!canAccessLeads ? "services" : "all");
  const [activeTab, setActiveTab] = useState(defaultTab); // "all" | "leads" | "services"

  const [editingReminder, setEditingReminder] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [editNote, setEditNote] = useState("");

  useEffect(() => {
    if (!loading && (location.hash === "#whatsapp-section" || location.search.includes("scroll=whatsapp"))) {
      const tryScroll = () => {
        const el = document.getElementById("whatsapp-section");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
        }
      };

      tryScroll();
      const t1 = setTimeout(tryScroll, 200);
      const t2 = setTimeout(tryScroll, 500);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [loading, location.hash, location.search]);

  const loadLostReminders = useCallback(async () => {
    if (!canAccessLeads) {
      setLostReminders([]);
      return;
    }
    try {
      const r = await api.get("/dashboard/lost-reminders");
      setLostReminders(r.data.items || []);
    } catch (err) {
      console.error("Failed to load lost reminders:", err);
    }
  }, [canAccessLeads]);

  const loadDashboardData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [statsRes] = await Promise.all([
        api.get("/dashboard/stats"),
        loadLostReminders(),
      ]);
      setStats(statsRes.data);

      api.get(`/whatsapp/status?userId=${user.id}`)
        .then(res => setWhatsAppConnected(res.data.status === "CONNECTED"))
        .catch(() => setWhatsAppConnected(false));

      api.get("/settings")
        .then(res => setEmailFollowupEnabled(res.data.email_followup_enabled || false))
        .catch(() => setEmailFollowupEnabled(false));
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      toast.error("Failed to load dashboard data");
      // Prevent infinite "Loading command center..." when stats never arrive
      setStats((prev) => prev || EMPTY_DASHBOARD_STATS);
    } finally {
      setLoading(false);
      if (isRefresh) {
        setTimeout(() => setRefreshing(false), 500);
        toast.success("Dashboard refreshed");
      }
    }
  }, [user.id, loadLostReminders]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const toggleReminderDone = async (leadId, currentStatus) => {
    const nextStatus = currentStatus === "completed" ? "pending" : "completed";
    try {
      await api.post(`/leads/${leadId}/lost-reminder`, { status: nextStatus });
      toast.success(nextStatus === "completed" ? "Reminder marked as completed!" : "Reminder marked as pending");
      loadLostReminders();
      api.get("/dashboard/stats").then((r) => setStats(r.data));
    } catch (e) {
      toast.error("Failed to update reminder");
    }
  };

  const removeReminder = async (leadId) => {
    if (!window.confirm("Are you sure you want to remove this reminder?")) return;
    try {
      await api.post(`/leads/${leadId}/lost-reminder`, { clear: true });
      toast.success("Reminder removed");
      loadLostReminders();
      api.get("/dashboard/stats").then((r) => setStats(r.data));
    } catch (e) {
      toast.error("Failed to remove reminder");
    }
  };

  const saveEditedReminder = async (e) => {
    e.preventDefault();
    if (!editingReminder || !editDate) return;
    try {
      await api.post(`/leads/${editingReminder.id}/lost-reminder`, {
        reminder_date: editDate,
        reminder_note: editNote,
      });
      toast.success("Reminder updated successfully!");
      setEditingReminder(null);
      loadLostReminders();
    } catch (err) {
      toast.error("Failed to update reminder");
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[450px] text-[#6B7280] gap-3">
        <div className="animate-spin text-[#002FA7]">
          <ArrowsClockwise size={32} weight="bold" />
        </div>
        <p className="text-sm font-semibold tracking-wide">Loading command center...</p>
        {!loading && !stats && (
          <button
            type="button"
            onClick={() => { setLoading(true); loadDashboardData(); }}
            className="mt-2 text-sm font-bold text-[#002FA7] underline"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  const currentMonthName = new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const todayStr = new Date().toISOString().split("T")[0];

  const filteredReminders = lostReminders.filter((r) => {
    if (reminderFilter === "pending" && r.lost_reminder_status === "completed") return false;
    if (reminderFilter === "completed" && r.lost_reminder_status !== "completed") return false;
    if (reminderSearch) {
      const q = reminderSearch.toLowerCase();
      const matchName = (r.name || "").toLowerCase().includes(q);
      const matchPhone = (r.phone || "").includes(q);
      const matchNote = (r.lost_reminder_note || "").toLowerCase().includes(q);
      if (!matchName && !matchPhone && !matchNote) return false;
    }
    return true;
  });

  const pendingRemindersCount = lostReminders.filter(r => r.lost_reminder_status !== "completed").length;
  const completedRemindersCount = lostReminders.filter(r => r.lost_reminder_status === "completed").length;

  const totalLeads = stats.total_leads || 0;
  const maxStatus = Math.max(1, ...Object.values(stats.by_status || {}));

  const services = stats.services || {};
  const totalContracts = services.total_contracts || 0;
  const maxContractType = Math.max(1, ...Object.values(services.by_type || {}));

  const showLeadsSection = canAccessLeads && (activeTab === "all" || activeTab === "leads");
  const showServicesSection = canAccessServices && (activeTab === "all" || activeTab === "services");

  return (
    <div data-testid="dashboard-page" className="space-y-6 md:space-y-8 pb-12">
      {/* Follow-up configuration warning alert */}
      {(!whatsAppConnected || !emailFollowupEnabled) && (
        <div className="border-l-4 border-[#F59E0B] bg-gradient-to-r from-[#FEF3C7] to-[#FFFBEB] p-4 text-[#92400E] shadow-xs" data-testid="followup-alert">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="relative flex h-3 w-3 mt-1 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F59E0B] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#D97706]"></span>
              </span>
              <div>
                <h4 className="font-heading font-black text-sm text-[#92400E] flex items-center gap-1.5">
                  <WarningCircle size={18} weight="fill" className="text-[#D97706]" />
                  {!whatsAppConnected && !emailFollowupEnabled
                    ? "Follow-up Automation Suspended"
                    : "Follow-up Configuration Warning"}
                </h4>
                <p className="text-xs text-[#B45309] mt-0.5 mb-2">
                  {!whatsAppConnected && !emailFollowupEnabled
                    ? "Automatic follow-up messages will not be sent because neither WhatsApp is connected nor Email follow-up is enabled."
                    : "One of the configured follow-up channels is currently unavailable."}
                </p>
                <div className="flex flex-wrap gap-4 text-xs font-semibold text-[#92400E]">
                  {!whatsAppConnected && (
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[#EF4444]"></span>
                      <span>WhatsApp disconnected &mdash; scan QR in WhatsApp Web below</span>
                    </span>
                  )}
                  {!emailFollowupEnabled && (
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[#F59E0B]"></span>
                      <span>
                        Email follow-ups disabled &mdash;{" "}
                        {["admin", "manager"].includes(user.role) ? (
                          <Link to="/settings" className="underline hover:text-[#78350F] font-bold">
                            Turn On in Settings
                          </Link>
                        ) : (
                          "Contact Admin"
                        )}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Welcome Header & View Switcher */}
      <div className="relative overflow-hidden bg-white border border-[#E5E7EB] p-6 sm:p-8 shadow-xs">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-50/60 via-indigo-50/30 to-transparent pointer-events-none -mr-20 -mt-20 rounded-full" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#F3F4F6] text-[#4B5563] border border-[#E5E7EB]">
                <Sparkle size={12} weight="fill" className="text-[#002FA7]" />
                {user.role === "salesperson" ? (userDept === "service" ? "Service Representative Hub" : userDept === "lead" ? "Sales Representative Hub" : "Sales & Service Hub") : user.role === "manager" ? "Operations & Team Control" : "Executive Command Center"}
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] font-mono text-[#6B7280]">
                <Calendar size={13} weight="bold" />
                {new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
              </span>
            </div>
            <h1 className="font-heading font-black text-2xl sm:text-3xl md:text-4xl tracking-tight text-[#111827]">
              Welcome, {user.name?.split(" ")[0]}.
            </h1>
            <p className="text-xs sm:text-sm text-[#6B7280] mt-1 max-w-2xl">
              {(canAccessLeads && stats.due_today > 0) || (canAccessServices && (services.due_today || 0) > 0) ? (
                <>
                  You have{" "}
                  {canAccessLeads && stats.due_today > 0 && (
                    <strong className="text-[#DC2626] font-bold">
                      {stats.due_today} lead follow-up{stats.due_today > 1 ? "s" : ""} due
                    </strong>
                  )}
                  {canAccessLeads && stats.due_today > 0 && canAccessServices && (services.due_today || 0) > 0 && " and "}
                  {canAccessServices && (services.due_today || 0) > 0 && (
                    <strong className="text-[#059669] font-bold">
                      {services.due_today} service follow-up{services.due_today > 1 ? "s" : ""} due
                    </strong>
                  )}
                  {canAccessServices && (services.expiring_soon || 0) > 0 && (
                    <span className="text-[#D97706] font-semibold">
                      {" "}(plus {services.expiring_soon} contract{services.expiring_soon > 1 ? "s" : ""} expiring in 30 days)
                    </span>
                  )}
                </>
              ) : (
                <>All scheduled follow-ups and communications are up to date</>
              )}
              {canAccessLeads && pendingRemindersCount > 0 && (
                <>, and <strong className="text-[#D97706] font-bold">{pendingRemindersCount} lost lead reminder{pendingRemindersCount > 1 ? "s" : ""}</strong> set for {currentMonthName}.</>
              )}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 shrink-0 self-start md:self-center">
            {/* View Mode Segmented Controls (Only show if user has access to both) */}
            {canAccessLeads && canAccessServices && (
              <div className="flex items-center bg-[#F3F4F6] p-1 border border-[#E5E7EB] shadow-2xs">
                <button
                  onClick={() => setActiveTab("all")}
                  className={`px-3 py-1.5 text-xs font-bold transition-all ${
                    activeTab === "all"
                      ? "bg-white text-[#002FA7] shadow-xs border border-gray-200"
                      : "text-[#4B5563] hover:text-[#111827]"
                  }`}
                >
                  All Overview
                </button>
                <button
                  onClick={() => setActiveTab("leads")}
                  className={`px-3 py-1.5 text-xs font-bold transition-all ${
                    activeTab === "leads"
                      ? "bg-[#002FA7] text-white shadow-xs"
                      : "text-[#4B5563] hover:text-[#111827]"
                  }`}
                >
                  Leads & Sales
                </button>
                <button
                  onClick={() => setActiveTab("services")}
                  className={`px-3 py-1.5 text-xs font-bold transition-all ${
                    activeTab === "services"
                      ? "bg-[#059669] text-white shadow-xs"
                      : "text-[#4B5563] hover:text-[#111827]"
                  }`}
                >
                  Service CRM
                </button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={() => loadDashboardData(true)}
                disabled={refreshing}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-[#374151] bg-white border border-[#D1D5DB] hover:bg-[#F9FAFB] hover:border-[#9CA3AF] transition-colors shadow-2xs"
                title="Refresh Dashboard Data"
              >
                <ArrowsClockwise size={14} weight="bold" className={refreshing ? "animate-spin text-[#002FA7]" : ""} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 1. LEADS PIPELINE KPI METRICS (Shown on "all" and "leads") */}
      {/* ========================================================================= */}
      {showLeadsSection && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target size={18} weight="bold" className="text-[#002FA7]" />
              <h2 className="font-heading font-black text-sm uppercase tracking-wider text-[#111827]">
                Leads & Sales Pipeline Metrics
              </h2>
            </div>
            <Link to="/leads" className="text-xs font-bold text-[#002FA7] hover:underline flex items-center gap-1">
              <span>View Lead Management</span>
              <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            <KPICard
              icon={Target}
              label="Total Pipeline"
              value={totalLeads}
              subtext="Total leads managed"
              accent="#002FA7"
              bgAccent="bg-blue-50"
              borderAccent="border-blue-100"
              big
              testid="metric-total"
              to="/leads"
            />
            <KPICard
              icon={Clock}
              label="Due Today"
              value={stats.due_today}
              subtext={stats.due_today > 0 ? "View due follow-ups" : "All caught up"}
              accent="#EF4444"
              bgAccent="bg-red-50"
              borderAccent="border-red-100"
              badge={stats.due_today > 0 ? "ACTION" : null}
              testid="metric-due"
              to="/leads?followup_status=due_today"
            />
            <KPICard
              icon={Bell}
              label="Lost Reminders"
              value={stats.lost_reminders_count || 0}
              subtext={`${pendingRemindersCount} pending this month`}
              accent="#D97706"
              bgAccent="bg-amber-50"
              borderAccent="border-amber-100"
              testid="metric-lost-reminders"
              to="/leads?reminder=due_this_month"
            />
            <KPICard
              icon={TrendUp}
              label="Converted"
              value={stats.by_status?.converted || 0}
              subtext="Successfully closed"
              accent="#10B981"
              bgAccent="bg-emerald-50"
              borderAccent="border-emerald-100"
              testid="metric-converted"
              to="/leads?status=converted"
            />
            <KPICard
              icon={ChartLineUp}
              label="Conversion Rate"
              value={`${stats.converted_rate}%`}
              subtext="Win efficiency"
              accent="#7C3AED"
              bgAccent="bg-purple-50"
              borderAccent="border-purple-100"
              testid="metric-rate"
              to="/leads?status=converted"
            />
            <KPICard
              icon={Money}
              label="Closed Revenue"
              value={`₹${(stats.total_amount || 0).toLocaleString("en-IN")}`}
              subtext="Won deal value"
              accent="#059669"
              bgAccent="bg-green-50"
              borderAccent="border-green-100"
              testid="metric-amount"
              to="/leads?status=converted"
            />
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 2. SERVICE CRM KPI METRICS (Shown on "all" and "services") */}
      {/* ========================================================================= */}
      {showServicesSection && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench size={18} weight="bold" className="text-[#059669]" />
              <h2 className="font-heading font-black text-sm uppercase tracking-wider text-[#111827]">
                Service CRM & Contracts Overview
              </h2>
            </div>
            <Link to="/service-crm" className="text-xs font-bold text-[#059669] hover:underline flex items-center gap-1">
              <span>View Service CRM</span>
              <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            <KPICard
              icon={Wrench}
              label="Total Contracts"
              value={totalContracts}
              subtext="All service records"
              accent="#002FA7"
              bgAccent="bg-blue-50"
              borderAccent="border-blue-100"
              big
              testid="metric-svc-total"
              to="/service-crm"
            />
            <KPICard
              icon={Clock}
              label="Service FU Due"
              value={services.due_today || 0}
              subtext={(services.due_today || 0) > 0 ? "Follow-ups due now" : "All up to date"}
              accent="#EF4444"
              bgAccent="bg-red-50"
              borderAccent="border-red-100"
              badge={(services.due_today || 0) > 0 ? "ACTION" : null}
              testid="metric-svc-due"
              to="/service-crm?followup_status=due_today"
            />
            <KPICard
              icon={Hourglass}
              label="Expiring (30d)"
              value={services.expiring_soon || 0}
              subtext="Renewal priority"
              accent="#D97706"
              bgAccent="bg-amber-50"
              borderAccent="border-amber-100"
              badge={(services.expiring_soon || 0) > 0 ? "RENEW" : null}
              testid="metric-svc-expiring"
              to="/service-crm?followup_status=expiring_30_days"
            />
            <KPICard
              icon={ShieldCheck}
              label="Active Coverage"
              value={services.active_contracts || 0}
              subtext="Under warranty / AMC"
              accent="#10B981"
              bgAccent="bg-emerald-50"
              borderAccent="border-emerald-100"
              testid="metric-svc-active"
              to="/service-crm?status=active"
            />
            <KPICard
              icon={WarningCircle}
              label="Expired / Out"
              value={services.expired || 0}
              subtext="Opportunity for AMC"
              accent="#DC2626"
              bgAccent="bg-rose-50"
              borderAccent="border-rose-100"
              testid="metric-svc-expired"
              to="/service-crm?status=expired"
            />
            <KPICard
              icon={Money}
              label="Service Value"
              value={`₹${(services.total_amount || 0).toLocaleString("en-IN")}`}
              subtext="Contract value"
              accent="#059669"
              bgAccent="bg-green-50"
              borderAccent="border-green-100"
              testid="metric-svc-revenue"
              to="/service-crm"
            />
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 3. PRIORITY WORK QUEUE: Top Due Leads & Due Service Contracts */}
      {/* ========================================================================= */}
      <div className="space-y-6">
        {/* Top Due Service Contracts (When on "all" or "services") */}
        {showServicesSection && (
          <section className="border border-[#E5E7EB] bg-white p-5 sm:p-6 shadow-2xs">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#F3F4F6]">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-emerald-50 text-[#059669] border border-emerald-100">
                  <Wrench size={20} weight="bold" />
                </div>
                <div>
                  <h3 className="font-heading font-black text-lg text-[#111827] flex items-center gap-2">
                    Service Priority &mdash; Follow-ups & Expiring Contracts
                    {services.top_due_services?.length > 0 && (
                      <span className="text-[11px] font-mono font-bold bg-emerald-100 text-[#059669] px-2 py-0.5 rounded-full">
                        {services.top_due_services.length} attention
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-[#6B7280]">
                    Active service contracts with follow-ups due or upcoming renewal expiry within 30 days.
                  </p>
                </div>
              </div>
              <Link
                to="/service-crm?followup_status=due_today"
                className="text-xs font-bold uppercase tracking-wider text-[#059669] hover:underline inline-flex items-center gap-1"
              >
                <span>View All Due Services</span>
                <ArrowRight size={12} weight="bold" />
              </Link>
            </div>

            {services.top_due_services?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                {services.top_due_services.map((c) => {
                  const rawType = (c.contract_type || "other").toLowerCase();
                  const typeLabel = CONTRACT_TYPE_LABELS[rawType] || rawType.replace("_", " ").toUpperCase();
                  const typeCol = CONTRACT_TYPE_COLORS[rawType] || "#059669";
                  const isExpSoon = c.expiry_date && c.expiry_date <= (new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0]);

                  return (
                    <Link
                      key={c.id}
                      to={`/service-crm/${c.id}`}
                      data-testid={`due-service-${c.id}`}
                      className="group block p-3.5 border border-[#E5E7EB] hover:border-[#059669] bg-[#FAFDFB] hover:bg-white hover:shadow-xs transition-all relative"
                    >
                      <div className="flex items-start justify-between gap-1 mb-1.5">
                        <span
                          className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border"
                          style={{ color: typeCol, borderColor: typeCol, backgroundColor: `${typeCol}10` }}
                        >
                          {typeLabel}
                        </span>
                        <ArrowSquareOut size={13} className="text-[#9CA3AF] group-hover:text-[#059669] transition-colors" />
                      </div>
                      <div className="font-bold text-sm text-[#111827] group-hover:text-[#059669] truncate">
                        {c.customer_name || "Customer"}
                      </div>
                      <div className="text-[11px] text-[#6B7280] truncate font-mono mt-0.5 flex items-center justify-between">
                        <span>{c.customer_phone || "No phone"}</span>
                        {c.card_no && <span className="text-[10px] text-gray-500 font-bold">#{c.card_no}</span>}
                      </div>
                      <div className="text-[11px] text-[#4B5563] truncate mt-1 pt-1.5 border-t border-[#F3F4F6] flex items-center justify-between">
                        <span className="truncate">{c.product_name || "Service Product"}</span>
                        {isExpSoon && (
                          <span className="text-[9px] font-bold uppercase text-[#D97706] bg-amber-50 px-1 py-0.2 rounded shrink-0 ml-1">
                            Exp: {c.expiry_date ? new Date(c.expiry_date).toLocaleDateString('en-IN') : 'Soon'}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 bg-[#F9FAFB] border border-dashed border-[#E5E7EB]">
                <CheckCircle size={32} weight="fill" className="mx-auto text-[#059669] mb-2 opacity-80" />
                <div className="text-sm font-bold text-[#111827]">No urgent service follow-ups due!</div>
                <div className="text-xs text-[#6B7280] mt-0.5">All customer contracts and service schedules are currently in order.</div>
              </div>
            )}
          </section>
        )}

        {/* Top 5 Due Leads Quick Action Section (When on "all" or "leads") */}
        {showLeadsSection && (
          <section className="border border-[#E5E7EB] bg-white p-5 sm:p-6 shadow-2xs">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#F3F4F6]">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-red-50 text-[#DC2626] border border-red-100">
                  <Clock size={20} weight="bold" />
                </div>
                <div>
                  <h3 className="font-heading font-black text-lg text-[#111827] flex items-center gap-2">
                    Lead Priority &mdash; Follow-ups Requiring Attention
                    {stats.top_due_leads?.length > 0 && (
                      <span className="text-[11px] font-mono font-bold bg-red-100 text-[#DC2626] px-2 py-0.5 rounded-full">
                        {stats.top_due_leads.length} due
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-[#6B7280]">
                    Leads with automated or scheduled follow-ups pending right now.
                  </p>
                </div>
              </div>
              <Link
                to="/leads?followup_status=due_today"
                className="text-xs font-bold uppercase tracking-wider text-[#002FA7] hover:underline inline-flex items-center gap-1"
              >
                <span>View All Due Today</span>
                <ArrowRight size={12} weight="bold" />
              </Link>
            </div>

            {stats.top_due_leads?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                {stats.top_due_leads.map((l) => (
                  <Link
                    key={l.id}
                    to={`/leads/${l.id}`}
                    data-testid={`due-lead-${l.id}`}
                    className="group block p-3.5 border border-[#E5E7EB] hover:border-[#002FA7] bg-[#FAFBFD] hover:bg-white hover:shadow-xs transition-all relative"
                  >
                    <div className="flex items-start justify-between gap-1 mb-1.5">
                      <span
                        className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border"
                        style={{ color: STATUS_COLORS[l.status], borderColor: STATUS_COLORS[l.status] }}
                      >
                        {STATUS_LABELS[l.status]}
                      </span>
                      <ArrowSquareOut size={13} className="text-[#9CA3AF] group-hover:text-[#002FA7] transition-colors" />
                    </div>
                    <div className="font-bold text-sm text-[#111827] group-hover:text-[#002FA7] truncate">
                      {l.name}
                    </div>
                    <div className="text-[11px] text-[#6B7280] truncate font-mono mt-0.5">
                      {l.phone || "No phone"}
                    </div>
                    <div className="text-[11px] text-[#4B5563] truncate mt-1 pt-1.5 border-t border-[#F3F4F6]">
                      {l.product_interest || l.company || "Direct inquiry"}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 bg-[#F9FAFB] border border-dashed border-[#E5E7EB]">
                <CheckCircle size={32} weight="fill" className="mx-auto text-[#10B981] mb-2 opacity-80" />
                <div className="text-sm font-bold text-[#111827]">No lead follow-ups due right now!</div>
                <div className="text-xs text-[#6B7280] mt-0.5">You're all caught up with your scheduled communications.</div>
              </div>
            )}
          </section>
        )}

        {/* Lost Lead Reminders Section (When on "all" or "leads") */}
        {showLeadsSection && (
          <section
            className="border border-[#FCD34D] bg-white shadow-xs overflow-hidden"
            data-testid="lost-reminders-section"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#FFFBEB] via-[#FEF3C7]/60 to-white p-5 sm:p-6 border-b border-[#FDE68A]">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-start sm:items-center gap-3">
                  <div className="p-2.5 bg-white text-[#D97706] border border-[#FCD34D] shadow-2xs shrink-0">
                    <Bell size={22} weight="bold" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-black text-xl text-[#111827] tracking-tight">
                        Lost Lead Follow-up Reminders
                      </h3>
                      <span className="text-xs font-bold font-mono bg-[#D97706] text-white px-2.5 py-0.5 rounded-full shadow-2xs">
                        {currentMonthName}
                      </span>
                    </div>
                    <p className="text-xs text-[#78350F] mt-0.5">
                      Client reminders scheduled for this month. Review and connect manually whenever ready.
                    </p>
                  </div>
                </div>

                {/* Filters & Search */}
                <div className="flex flex-wrap items-center gap-2.5 self-start lg:self-auto">
                  <div className="relative min-w-[180px]">
                    <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input
                      type="text"
                      placeholder="Search name, phone..."
                      value={reminderSearch}
                      onChange={(e) => setReminderSearch(e.target.value)}
                      className="w-full pl-7 pr-2.5 py-1.5 text-xs bg-white border border-[#D1D5DB] focus:border-[#002FA7] focus:outline-none placeholder-[#9CA3AF]"
                    />
                  </div>
                  <div className="flex items-center bg-white border border-[#D1D5DB] p-0.5 shadow-2xs">
                    <button
                      onClick={() => setReminderFilter("all")}
                      className={`px-2.5 py-1 text-xs font-bold transition-colors ${
                        reminderFilter === "all"
                          ? "bg-[#111827] text-white"
                          : "text-[#4B5563] hover:text-[#111827]"
                      }`}
                    >
                      All ({lostReminders.length})
                    </button>
                    <button
                      onClick={() => setReminderFilter("pending")}
                      className={`px-2.5 py-1 text-xs font-bold transition-colors ${
                        reminderFilter === "pending"
                          ? "bg-[#D97706] text-white"
                          : "text-[#4B5563] hover:text-[#111827]"
                      }`}
                    >
                      Pending ({pendingRemindersCount})
                    </button>
                    <button
                      onClick={() => setReminderFilter("completed")}
                      className={`px-2.5 py-1 text-xs font-bold transition-colors ${
                        reminderFilter === "completed"
                          ? "bg-[#10B981] text-white"
                          : "text-[#4B5563] hover:text-[#111827]"
                      }`}
                    >
                      Done ({completedRemindersCount})
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Table or Empty State */}
            {filteredReminders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] bg-[#FAFBFD] text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">
                      <th className="py-3 px-4">Client Name</th>
                      <th className="py-3 px-3">Phone</th>
                      <th className="py-3 px-3">Scheduled Date</th>
                      <th className="py-3 px-3">Follow-up Note</th>
                      {user?.role !== "salesperson" && <th className="py-3 px-3">Assigned To</th>}
                      <th className="py-3 px-3">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {filteredReminders.map((l) => {
                      const dateObj = new Date(l.lost_reminder_date);
                      const isToday = l.lost_reminder_date === todayStr;
                      const isOverdue = l.lost_reminder_status !== "completed" && l.lost_reminder_date < todayStr;
                      const isDone = l.lost_reminder_status === "completed";

                      return (
                        <tr
                          key={l.id}
                          className={`hover:bg-[#FFFDF7] transition-colors text-xs ${
                            isDone ? "bg-gray-50/50 opacity-80" : ""
                          }`}
                        >
                          <td className="py-3 px-4">
                            <Link
                              to={`/leads/${l.id}`}
                              className="font-bold text-sm text-[#002FA7] hover:underline flex items-center gap-1 group"
                            >
                              <span>{l.name}</span>
                              <ArrowSquareOut size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                            </Link>
                            <div className="text-[11px] text-[#6B7280]">
                              {l.company || l.product_interest || "Lost Client"}
                            </div>
                          </td>
                          <td className="py-3 px-3 font-mono text-[#374151] whitespace-nowrap">
                            {l.phone || "—"}
                          </td>
                          <td className="py-3 px-3 whitespace-nowrap">
                            <div className="font-mono font-bold text-[#111827]">
                              {dateObj.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                            </div>
                            {isOverdue && (
                              <span className="text-[9px] font-bold uppercase text-[#DC2626] bg-red-50 border border-red-200 px-1.5 py-0.2 rounded mt-0.5 inline-block">
                                ⚠️ Overdue
                              </span>
                            )}
                            {isToday && (
                              <span className="text-[9px] font-bold uppercase text-[#D97706] bg-amber-50 border border-amber-200 px-1.5 py-0.2 rounded mt-0.5 inline-block">
                                ⏳ Due Today
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 max-w-[260px]">
                            <div
                              className="text-xs text-[#4B5563] truncate"
                              title={l.lost_reminder_note || "No message note"}
                            >
                              {l.lost_reminder_note || <span className="text-gray-400 italic">No notes</span>}
                            </div>
                          </td>
                          {user?.role !== "salesperson" && (
                            <td className="py-3 px-3 text-[#4B5563] text-xs whitespace-nowrap">
                              {l.salesperson_name || <span className="text-gray-400">—</span>}
                            </td>
                          )}
                          <td className="py-3 px-3 whitespace-nowrap">
                            <span
                              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${
                                isDone
                                  ? "border-[#10B981] bg-green-50 text-[#059669]"
                                  : isOverdue
                                  ? "border-[#DC2626] bg-red-50 text-[#DC2626]"
                                  : isToday
                                  ? "border-[#D97706] bg-amber-50 text-[#B45309]"
                                  : "border-[#3B82F6] bg-blue-50 text-[#1D4ED8]"
                              }`}
                            >
                              {isDone ? "✓ Done" : isOverdue ? "Overdue" : isToday ? "Today" : "Pending"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingReminder(l);
                                  setEditDate(l.lost_reminder_date || "");
                                  setEditNote(l.lost_reminder_note || "");
                                }}
                                className="border border-[#B45309] text-[#B45309] bg-white px-2.5 py-1 text-[11px] font-bold hover:bg-[#FEF3C7] transition-colors"
                                title="Edit reminder date and note"
                              >
                                Edit Reminder
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleReminderDone(l.id, l.lost_reminder_status)}
                                className={
                                  !isDone
                                    ? "bg-[#10B981] text-white px-2.5 py-1 text-[11px] font-bold hover:bg-[#059669] inline-flex items-center gap-1 transition-colors shadow-2xs"
                                    : "border border-[#10B981] bg-green-50 text-[#059669] px-2.5 py-1 text-[11px] font-bold hover:bg-green-100 inline-flex items-center gap-1 transition-colors"
                                }
                                title={!isDone ? "Mark reminder as completed" : "Click to mark pending"}
                              >
                                <Check size={12} weight="bold" /> {isDone ? "✓ Done" : "Mark Done"}
                              </button>
                              <button
                                type="button"
                                onClick={() => removeReminder(l.id)}
                                className="text-[#EF4444] border border-[#EF4444] bg-white px-2 py-1 text-[11px] font-bold hover:bg-red-50 inline-flex items-center gap-1 transition-colors"
                                title="Remove reminder"
                              >
                                <Trash size={12} weight="bold" /> Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-10 text-center bg-[#FDFBF7] p-6">
                <Bell size={32} weight="bold" className="mx-auto text-[#D97706] mb-2 opacity-60" />
                <h4 className="font-heading font-bold text-sm text-[#92400E] mb-1">
                  {reminderSearch
                    ? "No matching reminders found"
                    : `No Lost Lead Reminders for ${currentMonthName}`}
                </h4>
                <p className="text-xs text-[#78350F] max-w-md mx-auto mb-3">
                  {reminderSearch
                    ? "Try adjusting your search keywords or clear the filter."
                    : "When marking a lead as Lost, salespeople can set a reminder to re-engage with them later. Reminders for this month will appear here."}
                </p>
                {reminderSearch ? (
                  <button
                    onClick={() => setReminderSearch("")}
                    className="text-xs font-bold text-[#002FA7] hover:underline"
                  >
                    Clear search
                  </button>
                ) : (
                  <Link
                    to="/leads?status=lost"
                    className="inline-flex items-center gap-1 text-xs font-bold text-[#002FA7] hover:underline"
                  >
                    Browse Lost Leads <ArrowRight size={12} weight="bold" />
                  </Link>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      {/* ========================================================================= */}
      {/* 4. ANALYTICS & DISTRIBUTION BREAKDOWNS (Leads & Services) */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Lead Pipeline Funnel by Stage (When on "all" or "leads") */}
        {showLeadsSection && (
          <section className="border border-[#E5E7EB] bg-white p-4 sm:p-5 shadow-2xs lg:col-span-2 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#F3F4F6]">
                <div className="flex items-center gap-2">
                  <Target size={16} className="text-[#002FA7]" />
                  <h3 className="font-heading font-black text-sm uppercase tracking-wider text-[#111827]">
                    Lead Pipeline Funnel (16 Stages)
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono font-bold text-[#002FA7] bg-blue-50 px-2 py-0.5 border border-blue-100">
                    {totalLeads} Total Leads
                  </span>
                  <Link to="/leads" className="text-[11px] font-bold text-[#002FA7] hover:underline flex items-center gap-0.5">
                    <span>Pipeline Board</span>
                    <ArrowRight size={10} weight="bold" />
                  </Link>
                </div>
              </div>

              {/* Multi-segment Pipeline Funnel Bar */}
              {totalLeads > 0 && (
                <div className="mb-3.5">
                  <div className="h-2 w-full bg-[#F3F4F6] rounded-full overflow-hidden flex border border-[#E5E7EB]/60">
                    {PIPELINE_PHASES.map((phase) => {
                      const pTotal = phase.stages.reduce((sum, s) => sum + (stats?.by_status?.[s.key] || 0), 0);
                      const pPct = (pTotal / totalLeads) * 100;
                      if (pPct <= 0) return null;
                      return (
                        <div
                          key={phase.id}
                          style={{ width: `${pPct}%`, backgroundColor: phase.accent }}
                          className="h-full transition-all duration-500 hover:opacity-90"
                          title={`${phase.title}: ${pTotal} leads (${Math.round(pPct)}%)`}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 4 Funnel Phase Columns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2.5">
                {PIPELINE_PHASES.map((phase) => {
                  const pTotal = phase.stages.reduce((sum, s) => sum + (stats?.by_status?.[s.key] || 0), 0);
                  const pPct = totalLeads > 0 ? Math.round((pTotal / totalLeads) * 100) : 0;
                  return (
                    <div
                      key={phase.id}
                      className={`p-2.5 rounded-xs border ${phase.borderAccent} ${phase.bgAccent} flex flex-col justify-between`}
                    >
                      <div className="flex items-center justify-between pb-1.5 mb-1.5 border-b border-[#E5E7EB]/60">
                        <span className="text-[11px] font-bold text-[#111827] flex items-center gap-1.5 truncate">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: phase.accent }} />
                          <span className="truncate">{phase.title}</span>
                        </span>
                        <span className="font-mono text-[10px] font-bold text-[#374151] bg-white px-1.5 py-0.5 rounded-xs border border-[#E5E7EB] shrink-0 ml-1">
                          {pTotal} <span className="text-gray-400 font-normal">({pPct}%)</span>
                        </span>
                      </div>
                      <div className="space-y-1">
                        {phase.stages.map((st) => {
                          const count = stats?.by_status?.[st.key] || 0;
                          const pct = totalLeads > 0 ? Math.round((count / totalLeads) * 100) : 0;
                          const col = STATUS_COLORS[st.key] || phase.accent;
                          const hasCount = count > 0;
                          return (
                            <Link
                              key={st.key}
                              to={`/leads?status=${st.key}`}
                              className={`flex items-center justify-between px-2 py-1 text-xs rounded-xs transition-all ${
                                hasCount
                                  ? "bg-white hover:bg-[#FAFDFB] border border-[#E5E7EB] hover:border-[#002FA7] shadow-2xs group"
                                  : "bg-white/40 hover:bg-white border border-transparent hover:border-gray-200 text-gray-400 hover:text-gray-700"
                              }`}
                              title={`View ${st.label} leads`}
                            >
                              <div className="flex items-center gap-1.5 truncate mr-1">
                                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: col }} />
                                <span className={`truncate text-[11px] ${hasCount ? "font-semibold text-[#1F2937] group-hover:text-[#002FA7]" : "text-gray-500"}`}>
                                  {st.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 shrink-0 font-mono text-[11px]">
                                <span className={hasCount ? "font-bold text-[#111827] group-hover:text-[#002FA7]" : "text-gray-400"}>
                                  {count}
                                </span>
                                {hasCount && (
                                  <span className="text-[9px] text-gray-400">({pct}%)</span>
                                )}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Lead Source / Channel Breakdown (When on "all" or "leads") */}
        {showLeadsSection && (
          <section className="border border-[#E5E7EB] bg-white p-4 sm:p-5 shadow-2xs lg:col-span-1 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#F3F4F6]">
                <div className="flex items-center gap-2">
                  <Buildings size={16} className="text-[#002FA7]" />
                  <h3 className="font-heading font-black text-sm uppercase tracking-wider text-[#111827]">
                    Acquisition Channel
                  </h3>
                </div>
                <Link to="/leads" className="text-[11px] font-bold text-[#002FA7] hover:underline flex items-center gap-0.5">
                  <span>Filter</span>
                  <ArrowRight size={10} weight="bold" />
                </Link>
              </div>

              {/* Multi-segment Channel Mix Bar */}
              {totalLeads > 0 && (
                <div className="mb-3.5">
                  <div className="h-2 w-full bg-[#F3F4F6] rounded-full overflow-hidden flex border border-[#E5E7EB]/60">
                    {Object.entries(CHANNEL_LABELS).map(([k, label]) => {
                      const v = stats.by_channel?.[k] ?? stats.by_source?.[k] ?? 0;
                      const pct = (v / totalLeads) * 100;
                      if (pct <= 0) return null;
                      return (
                        <div
                          key={k}
                          style={{ width: `${pct}%`, backgroundColor: CHANNEL_COLORS[k] || "#002FA7" }}
                          className="h-full transition-all duration-500 hover:opacity-90"
                          title={`${label}: ${v} leads (${Math.round(pct)}%)`}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-1.5">
                {Object.entries(CHANNEL_LABELS).map(([k, label]) => {
                  const v = stats.by_channel?.[k] ?? stats.by_source?.[k] ?? 0;
                  const pct = totalLeads > 0 ? Math.round((v / totalLeads) * 100) : 0;
                  const col = CHANNEL_COLORS[k] || "#002FA7";
                  const hasCount = v > 0;
                  return (
                    <Link
                      key={k}
                      to={`/leads?source=${k}`}
                      className={`flex justify-between items-center py-1.5 px-2 rounded-xs border transition-all group ${
                        hasCount
                          ? "bg-[#FAFBFD] border-[#E5E7EB] hover:bg-white hover:border-[#002FA7] shadow-2xs"
                          : "bg-[#F9FAFB]/70 border-[#E5E7EB]/70 opacity-75 hover:opacity-100"
                      }`}
                      title={`Filter ${label} leads`}
                    >
                      <span className="text-[11px] font-semibold text-[#374151] group-hover:text-[#002FA7] flex items-center gap-1.5 truncate mr-1">
                        <span
                          className="w-1.5 h-1.5 rounded-full inline-block shrink-0"
                          style={{ backgroundColor: col }}
                        />
                        <span className="truncate">{label}</span>
                      </span>
                      <div className="font-mono text-xs text-right shrink-0">
                        <span className={`font-bold ${hasCount ? "text-[#111827] group-hover:text-[#002FA7]" : "text-gray-400"}`}>
                          {v}
                        </span>
                        <span className="text-gray-400 text-[9px] ml-1">({pct}%)</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Service Contracts by Type (When on "all" or "services") */}
        {showServicesSection && (
          <section className="border border-[#E5E7EB] bg-white p-4 sm:p-5 shadow-2xs lg:col-span-2 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#F3F4F6]">
                <div className="flex items-center gap-2">
                  <Wrench size={16} className="text-[#059669]" />
                  <h3 className="font-heading font-black text-sm uppercase tracking-wider text-[#111827]">
                    Service Contracts by Type (7 Categories)
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono font-bold text-[#059669] bg-emerald-50 px-2 py-0.5 border border-emerald-100">
                    {totalContracts} Total Contracts
                  </span>
                  <Link to="/service-crm" className="text-[11px] font-bold text-[#059669] hover:underline flex items-center gap-0.5">
                    <span>View CRM</span>
                    <ArrowRight size={10} weight="bold" />
                  </Link>
                </div>
              </div>

              {/* Multi-segment Contract Mix Bar */}
              {totalContracts > 0 && (
                <div className="mb-3.5">
                  <div className="h-2 w-full bg-[#F3F4F6] rounded-full overflow-hidden flex border border-[#E5E7EB]/60">
                    {Object.entries(CONTRACT_TYPE_LABELS).map(([k, label]) => {
                      const v = services.by_type?.[k] || 0;
                      const pct = (v / totalContracts) * 100;
                      if (pct <= 0) return null;
                      return (
                        <div
                          key={k}
                          style={{ width: `${pct}%`, backgroundColor: CONTRACT_TYPE_COLORS[k] || "#059669" }}
                          className="h-full transition-all duration-500 hover:opacity-90"
                          title={`${label}: ${v} contracts (${Math.round(pct)}%)`}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {Object.entries(services.by_type || {}).map(([k, v]) => {
                  const pct = totalContracts > 0 ? Math.round((v / totalContracts) * 100) : 0;
                  const label = CONTRACT_TYPE_LABELS[k] || k.replace("_", " ").toUpperCase();
                  const col = CONTRACT_TYPE_COLORS[k] || "#059669";
                  const hasCount = v > 0;
                  return (
                    <Link
                      key={k}
                      to={`/service-crm?contract_type=${k}`}
                      className={`group p-2 rounded-xs border transition-all flex flex-col justify-between ${
                        hasCount
                          ? "bg-[#FAFDFB] border-[#E5E7EB] hover:border-[#059669] hover:bg-white hover:shadow-2xs"
                          : "bg-[#F9FAFB]/70 border-[#E5E7EB]/70 hover:border-gray-300 opacity-75 hover:opacity-100"
                      }`}
                      title={`Filter ${label} contracts`}
                    >
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="flex items-center gap-1.5 truncate font-semibold text-[#374151] group-hover:text-[#111827]">
                          <span
                            className="w-1.5 h-1.5 rounded-full inline-block shrink-0"
                            style={{ backgroundColor: col }}
                          />
                          <span className="truncate text-[11px]">{label}</span>
                        </span>
                        <span className={`font-mono text-xs font-bold ${hasCount ? "text-[#111827] group-hover:text-[#059669]" : "text-gray-400"}`}>
                          {v}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <div className="h-1 bg-[#F3F4F6] overflow-hidden rounded-full flex-1">
                          <div
                            className="h-full rounded-full transition-all duration-300"
                            style={{
                              width: `${Math.max(v > 0 ? 5 : 0, (v / maxContractType) * 100)}%`,
                              backgroundColor: col,
                            }}
                          />
                        </div>
                        <span className="text-[10px] font-mono text-gray-400 shrink-0">{pct}%</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Service Contracts Status Breakdown (When on "all" or "services") */}
        {showServicesSection && (
          <section className="border border-[#E5E7EB] bg-white p-4 sm:p-5 shadow-2xs lg:col-span-1 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#F3F4F6]">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={16} className="text-[#059669]" />
                  <h3 className="font-heading font-black text-sm uppercase tracking-wider text-[#111827]">
                    Contract Status & SLA
                  </h3>
                </div>
                <Link to="/service-crm" className="text-[11px] font-bold text-[#059669] hover:underline flex items-center gap-0.5">
                  <span>Filter</span>
                  <ArrowRight size={10} weight="bold" />
                </Link>
              </div>

              {/* Multi-segment Status Mix Bar */}
              {totalContracts > 0 && (
                <div className="mb-3.5">
                  <div className="h-2 w-full bg-[#F3F4F6] rounded-full overflow-hidden flex border border-[#E5E7EB]/60">
                    {Object.entries(CONTRACT_STATUS_LABELS).map(([k, label]) => {
                      const v = services.by_status?.[k] || 0;
                      const pct = (v / totalContracts) * 100;
                      if (pct <= 0) return null;
                      return (
                        <div
                          key={k}
                          style={{ width: `${pct}%`, backgroundColor: CONTRACT_STATUS_COLORS[k] || "#6B7280" }}
                          className="h-full transition-all duration-500 hover:opacity-90"
                          title={`${label}: ${v} contracts (${Math.round(pct)}%)`}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-1.5">
                {Object.entries(CONTRACT_STATUS_LABELS).map(([k, label]) => {
                  const v = services.by_status?.[k] || 0;
                  const pct = totalContracts > 0 ? Math.round((v / totalContracts) * 100) : 0;
                  const col = CONTRACT_STATUS_COLORS[k] || "#6B7280";
                  const hasCount = v > 0;
                  return (
                    <Link
                      key={k}
                      to={`/service-crm?status=${k}`}
                      className={`flex justify-between items-center py-1.5 px-2 rounded-xs border transition-all group ${
                        hasCount
                          ? "bg-[#FAFDFB] border-[#E5E7EB] hover:bg-white hover:border-[#059669] shadow-2xs"
                          : "bg-[#F9FAFB]/70 border-[#E5E7EB]/70 opacity-75 hover:opacity-100"
                      }`}
                      title={`Filter ${label} contracts`}
                    >
                      <span className="text-[11px] font-semibold text-[#374151] group-hover:text-[#059669] flex items-center gap-1.5 truncate mr-1">
                        <span
                          className="w-1.5 h-1.5 rounded-full inline-block shrink-0"
                          style={{ backgroundColor: col }}
                        />
                        <span className="truncate">{label}</span>
                      </span>
                      <div className="font-mono text-xs text-right shrink-0">
                        <span className={`font-bold ${hasCount ? "text-[#111827] group-hover:text-[#059669]" : "text-gray-400"}`}>
                          {v}
                        </span>
                        <span className="text-gray-400 text-[9px] ml-1">({pct}%)</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Team Performance for Manager / Admin (Combined Leads & Service) */}
        {stats.team && stats.team.length > 0 && (
          <section className="border border-[#E5E7EB] bg-white p-5 sm:p-6 shadow-2xs lg:col-span-3">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#F3F4F6]">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-indigo-50 text-[#002FA7] border border-indigo-100">
                  <Users size={20} weight="bold" />
                </div>
                <div>
                  <h3 className="font-heading font-black text-lg text-[#111827] tracking-tight">
                    Operations & Representative Leaderboard
                  </h3>
                  <p className="text-xs text-[#6B7280]">Representative quotas, deal closures, service contracts, and generated revenue</p>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b-2 border-[#111827] text-[10px] uppercase tracking-wider py-2 font-bold text-[#4B5563]">
                    <th className="py-2.5 px-3">Team Representative</th>
                    <th className="py-2.5 px-3 text-right">Leads (Total/Active)</th>
                    <th className="py-2.5 px-3 text-right">Won Deals</th>
                    <th className="py-2.5 px-3 text-right">Lead Revenue</th>
                    <th className="py-2.5 px-3 text-right">Service Contracts (Total/Active)</th>
                    <th className="py-2.5 px-3 text-right">Service Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F3F4F6]">
                  {stats.team.map((t, idx) => (
                    <tr
                      key={t.id}
                      className="hover:bg-[#F9FAFB] transition-colors text-xs"
                      data-testid={`team-row-${t.id}`}
                    >
                      <td className="py-3 px-3 font-bold text-[#111827]">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-[#F3F4F6] text-[#4B5563] text-[10px] font-mono font-bold flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <span>{t.name}</span>
                          {idx === 0 && <span className="text-xs">🏆</span>}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-xs">
                        <Link
                          to={`/leads?assigned_to=${t.id}`}
                          className="font-bold text-[#374151] hover:text-[#002FA7] hover:underline"
                          title={`View leads for ${t.name}`}
                        >
                          {t.total} / <span className="text-[#3B82F6]">{t.active}</span>
                        </Link>
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-xs">
                        <Link
                          to={`/leads?assigned_to=${t.id}&status=converted`}
                          className="text-[#10B981] hover:underline font-bold"
                          title={`View converted deals for ${t.name}`}
                        >
                          {t.converted}
                        </Link>
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-[#002FA7]">
                        ₹{(t.revenue || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-xs">
                        <Link
                          to={`/service-crm?assigned_to=${t.id}`}
                          className="font-bold text-[#374151] hover:text-[#059669] hover:underline"
                          title={`View service contracts for ${t.name}`}
                        >
                          {t.service_total || 0} / <span className="text-[#059669]">{t.service_active || 0}</span>
                        </Link>
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-[#059669]">
                        ₹{(t.service_revenue || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* WhatsApp Connection Drawer Section */}
        <div id="whatsapp-section" className="lg:col-span-3">
          <WhatsAppConnection />
        </div>
      </div>

      {/* Edit Reminder Modal */}
      {editingReminder && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full p-6 border border-[#111827] shadow-2xl animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-[#E5E7EB]">
              <div className="flex items-center gap-2">
                <Bell size={20} weight="bold" className="text-[#D97706]" />
                <div>
                  <h3 className="font-heading font-black text-base text-[#111827]">Edit Follow-up Reminder</h3>
                  <p className="text-xs text-[#6B7280]">
                    Client: <span className="font-bold text-[#002FA7]">{editingReminder.name}</span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditingReminder(null)}
                className="text-gray-400 hover:text-gray-700 text-lg font-bold p-1"
              >
                ✕
              </button>
            </div>
            <form onSubmit={saveEditedReminder} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#374151] mb-1">Reminder Date *</label>
                <input
                  type="date"
                  required
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#374151] mb-1">Follow-up Note / Draft Message</label>
                <textarea
                  rows={3}
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  placeholder="Notes or follow-up plan for when reminder is due..."
                  className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-[#F3F4F6]">
                <button
                  type="button"
                  onClick={() => setEditingReminder(null)}
                  className="px-4 py-2 border border-[#D1D5DB] text-xs font-bold text-[#4B5563] hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#002FA7] text-white text-xs font-bold hover:bg-[#00207A] transition-colors"
                >
                  Save Reminder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function KPICard({ icon: Icon, label, value, subtext, accent, bgAccent, borderAccent, big, badge, testid, to }) {
  const cardElement = (
    <div
      className={`border border-[#E5E7EB] bg-white p-4 sm:p-5 relative transition-all duration-200 h-full ${
        to
          ? "hover:-translate-y-1 hover:shadow-md hover:border-[#002FA7] cursor-pointer group"
          : "hover:-translate-y-0.5 hover:shadow-2xs"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#6B7280] truncate mr-1 group-hover:text-[#002FA7] transition-colors">
          {label}
        </span>
        <div className={`p-1.5 rounded-sm ${bgAccent} ${borderAccent} border shrink-0 group-hover:scale-110 transition-transform`}>
          <Icon size={16} weight="bold" style={{ color: accent }} />
        </div>
      </div>
      <div className="flex items-baseline gap-2">
        <div
          className={`font-heading font-black tracking-tight text-[#111827] group-hover:text-[#002FA7] transition-colors ${
            big ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl"
          }`}
        >
          {value}
        </div>
        {badge && (
          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.2 bg-red-100 text-[#DC2626] rounded">
            {badge}
          </span>
        )}
      </div>
      {subtext && (
        <div className="text-[11px] text-[#6B7280] mt-1 truncate flex items-center justify-between">
          <span>{subtext}</span>
          {to && (
            <ArrowRight size={12} weight="bold" className="text-[#9CA3AF] opacity-0 group-hover:opacity-100 group-hover:text-[#002FA7] transition-all ml-1 shrink-0" />
          )}
        </div>
      )}
    </div>
  );

  if (to) {
    return (
      <Link to={to} className="block h-full no-underline">
        {cardElement}
      </Link>
    );
  }

  return cardElement;
}
